import React, { ChangeEvent, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { compose } from 'recompose';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { centsToDollars } from '@mm/utils';
import BasicModalTemplate from '../../../../../common/modals/BasicModalTemplate';
import { ModalInstanceProps } from '../../../../../common/modals/interfaces';
import { useSubscriptionCRUDActions } from '@mm/state/actions/SubscriptionCRUDActions';
import { AddMorePagesTeamModalParams } from '@mm/state/modules/modalsState/actions';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { LogoAndText } from '../../common/LogoAndText/LogoAndText';
import PaymentMethodDetails from '../../common/PaymentMethodDetails/PaymentMethodDetails';
import './AddMorePagesTeamModal.pcss';
import { enhancedBillingPagesSelector } from '@mm/state/selectors/facebookPageSelector';

type OuterProps = AddMorePagesTeamModalParams & ModalInstanceProps;

type InnerProps = ReturnType<typeof mapStateToProps>;

type Props = OuterProps & InnerProps;

const AddMorePagesTeamModal = (props: Props) => {
  const {
    subscription,
    plan,
    closeModal,
    pagesCount,
    facebookPageId,
    company,
  } = props;
  const [additionalPages, setAdditionalPages] = useState(pagesCount);
  const [makingRequest, setMakingRequest] = useState(false);
  const extraPageAddOnPrice = plan.extraPageAddOnPrice
    ? plan.extraPageAddOnPrice
    : 1500;
  const proratedUnitPrice =
    subscription.extraPageAddOnPrice ||
    extraPageAddOnPrice * subscription.priceFactor;
  const onInputChange = (e: ChangeEvent<HTMLInputElement>) =>
    setAdditionalPages(Number(e.target.value));
  const price = Math.round(additionalPages * extraPageAddOnPrice);
  const proratedPrice = Math.round(additionalPages * proratedUnitPrice);

  const fbTeamPage = useSelector(enhancedBillingPagesSelector).find(
    p => p.subscription?.planType === 'team',
  );

  const fbPageTeamId = facebookPageId || fbTeamPage?.id;
  const subscriptionCRUDActions = useSubscriptionCRUDActions();
  const onConfirm = async () => {
    setMakingRequest(true);
    await subscriptionCRUDActions.addExtraPagesToTeam(
      subscription.id,
      company?.id || -1,
      additionalPages,
    );
    setTimeout(() => {
      fbPageTeamId &&
        subscriptionCRUDActions.getCurrentSubscription(fbPageTeamId, false);
    }, 1000);
    closeModal();
  };

  return (
    <BasicModalTemplate
      className="add-more-pages-team-modal"
      withMonkeyBackground={false}
    >
      <div className="add-more-pages-team-modal__header">
        <LogoAndText />

        <div className="add-more-pages-team-modal__team-name">TEAM</div>
        <div className="add-more-pages-team-modal__subtitle">
          Additional FB Pages
        </div>
      </div>

      <div className="add-more-pages-team-modal__body">
        <div className="add-more-pages-team-modal__question-text">
          How many pages would you like to buy?
        </div>

        <input
          type="number"
          min="1"
          onChange={onInputChange}
          value={additionalPages}
          className="add-more-pages-team-modal__input"
        />

        <div className="add-more-pages-team-modal__prorated-area">
          <div className="add-more-pages-team-modal__prorated-text">
            Pro-rated payment due today
          </div>
          <div className="add-more-pages-team-modal__prorated-amount">
            ${centsToDollars(proratedPrice)}
          </div>
        </div>
        <div className="add-more-pages-team-modal__amount-added-area">
          <div className="add-more-pages-team-modal__amount-added-text">
            Amount added to your monthly subscription
          </div>
          <div className="add-more-pages-team-modal__amount-added-amount">
            ${centsToDollars(price)}
          </div>
        </div>

        <PaymentMethodDetails />

        <PrimaryButton
          disabled={!additionalPages || makingRequest}
          onClick={onConfirm}
          className="add-more-pages-team-modal__button"
        >
          Confirm
        </PrimaryButton>
      </div>
    </BasicModalTemplate>
  );
};

const mapStateToProps = (state: StoreState) => ({
  company: activeCompanySelector(state),
});

const enhance = compose<InnerProps, OuterProps>(connect(mapStateToProps));

export default enhance(AddMorePagesTeamModal);
